import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowUpRight,
  FiShare2,
  FiClock,
  FiCheck,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogViewTracker from "../components/blog/BlogViewTracker";
import BlogRenderer from "../components/blog/BlogRenderer";
import { useSEO, SITE_URL, DEFAULT_IMAGE } from "../utils/seo";
import { BlogCard, blogDate, blogPath, fetchBlogApi } from "./Blogs";
import { readBlogBootstrap } from "../utils/blogBootstrap";
import "./Blogs.css";

export default function BlogDetail() {
  const { blogId, slug } = useParams();
  const navigate = useNavigate();
  const [initialBlog] = useState(() => readBlogBootstrap("article"));
  const [blog, setBlog] = useState(initialBlog),
    [related, setRelated] = useState([]),
    [error, setError] = useState(null),
    [loading, setLoading] = useState(!initialBlog),
    [retry, setRetry] = useState(0),
    [copied, setCopied] = useState(false),
    [progress, setProgress] = useState(0),
    [shareError, setShareError] = useState("");
  const bodyRef = useRef(null);
  const [sections, setSections] = useState([]);
  const structuredData = useMemo(
    () =>
      blog
        ? blog.seo?.structuredData || [
            {
              "@type": "BlogPosting",
              headline: blog.title,
              description: blog.excerpt,
              image: blog.coverImage || DEFAULT_IMAGE,
              datePublished: blog.publishedAt,
              dateModified: blog.updatedAt,
              author: { "@type": "Person", name: blog.author },
              publisher: {
                "@type": "Organization",
                name: "Instaport Delivery",
              },
              mainEntityOfPage: `${SITE_URL}${blogPath(blog)}`,
            },
          ]
        : [],
    [blog],
  );
  useSEO({
    title: blog?.title || "Instaport Journal",
    description:
      blog?.seo?.description ||
      blog?.excerpt ||
      "Stories from the Instaport team.",
    path: blog ? blogPath(blog) : `/${slug}/${blogId}`,
    image: blog?.seo?.image || blog?.coverImage || DEFAULT_IMAGE,
    type: "article",
    noIndex: error?.status === 404 && !blog,
    structuredData,
  });
  useEffect(() => {
    const controller = new AbortController();
    const initial = retry === 0 ? readBlogBootstrap("article") : null;
    setLoading(!initial);
    setBlog(initial);
    setError(null);
    setRelated([]);
    setCopied(false);
    setShareError("");
    if (!/^[a-z0-9]{8}$/i.test(blogId)) {
      setError({ status: 404, message: "This page could not be found." });
      setLoading(false);
      return () => controller.abort();
    }
    (initial ? Promise.resolve(initial) : fetchBlogApi(`/${blogId}`, controller.signal))
      .then((data) => {
        if (controller.signal.aborted) return;
        setBlog(data);
        setLoading(false);
        if (data.slug !== slug) navigate(blogPath(data), { replace: true });
        return fetchBlogApi(
          `?${new URLSearchParams({ category: data.category, limit: 4 })}`,
          controller.signal,
        ).then((relatedData) => {
          if (!controller.signal.aborted)
            setRelated(relatedData.items.filter((item) => item.blogId !== blogId).slice(0, 3));
        }).catch(() => {
          // Recommendations are optional; their failure must not hide the article.
        });
      })
      .catch((e) => {
        if (!controller.signal.aborted && e.name !== "AbortError") setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [blogId, slug, navigate, retry]);
  useEffect(() => {
    if (loading || !bodyRef.current) {
      setSections([]);
      return;
    }
    const headings = [
      ...bodyRef.current.querySelectorAll(".blog-content h2, .blog-content h3"),
    ];
    setSections(
      headings
        .map((heading, index) => {
          heading.id = `story-section-${index + 1}`;
          return {
            id: heading.id,
            text: heading.textContent.trim(),
            nested: heading.tagName === "H3",
          };
        })
        .filter((section) => section.text),
    );
    if (/^#story-section-\d+$/.test(window.location.hash)) {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
    }
  }, [blog, loading]);
  useEffect(() => {
    const scroll = () => {
      const el = document.getElementById("journal-article");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setProgress(
        Math.max(
          0,
          Math.min(
            100,
            (-rect.top / Math.max(1, rect.height - window.innerHeight)) * 100,
          ),
        ),
      );
    };
    window.addEventListener("scroll", scroll, { passive: true });
    scroll();
    return () => window.removeEventListener("scroll", scroll);
  }, [blog]);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);
  const share = async () => {
    try {
      const url = `${window.location.origin}${blogPath(blog)}`;
      if (navigator.share) await navigator.share({ title: blog.title, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch (e) {
      if (e.name !== "AbortError")
        setShareError(
          "Copy the address from your browser to share this story.",
        );
    }
  };
  return (
    <div className="journal-page">
      <div
        className="journal-reading-progress"
        style={{ width: `${progress}%` }}
      />
      <Navbar />
      <main className="journal-main journal-detail">
        <div className="journal-detail-navigation">
          <Link to="/blogs" className="journal-back">
            <FiArrowLeft /> Back to the journal
          </Link>
          <span>THE INSTAPORT JOURNAL</span>
        </div>
        {loading ? (
          <div className="journal-empty" role="status">
            Opening your next read…
          </div>
        ) : error && !blog ? (
          <div className="journal-empty" role="alert">
            <h1>
              {error.status === 404
                ? "This story isn’t available"
                : "We couldn’t load this story"}
            </h1>
            <p>{error.message}</p>
            {error.status !== 404 && (
              <button onClick={() => setRetry((v) => v + 1)}>Try again</button>
            )}
            <Link to="/blogs">
              Explore the journal <FiArrowUpRight />
            </Link>
          </div>
        ) : (
          blog && (
            <>
              <BlogViewTracker blogId={blog.blogId} />
              <article id="journal-article">
                <header className="journal-article-header">
                  <Link
                    className="journal-article-category"
                    to={`/blogs?category=${encodeURIComponent(blog.category)}`}
                  >
                    {blog.category}
                  </Link>
                  <h1>{blog.title}</h1>
                  <p className="journal-article-excerpt">{blog.excerpt}</p>
                  <div className="journal-article-meta">
                    <div className="journal-author-avatar">
                      {(blog.author || "Instaport Team").slice(0, 1)}
                    </div>
                    <div>
                      <strong>{blog.author || "Instaport Team"}</strong>
                      <time dateTime={blog.publishedAt}>
                        {blogDate(blog.publishedAt)}
                      </time>
                    </div>
                    <span className="journal-read-time">
                      <FiClock /> {blog.readingMinutes} min read
                    </span>
                    <button className="journal-share" onClick={share}>
                      {copied ? <FiCheck /> : <FiShare2 />}
                      {copied ? "Copied" : "Share story"}
                    </button>
                  </div>
                  {shareError && <p role="status">{shareError}</p>}
                </header>
                {blog.coverImage && (
                  <figure className="journal-article-cover">
                    <img
                      src={blog.coverImage}
                      alt={blog.coverAlt || ""}
                      decoding="async"
                    />
                  </figure>
                )}
                <div className="journal-article-layout">
                  <aside className="journal-article-rail">
                    {sections.length > 0 ? (
                      <nav aria-label="On this page">
                        <span className="journal-rail-label">
                          IN THIS STORY
                        </span>
                        <ol>
                          {sections.map((section) => (
                            <li
                              key={section.id}
                              className={section.nested ? "nested" : ""}
                            >
                              <a href={`#${section.id}`}>{section.text}</a>
                            </li>
                          ))}
                        </ol>
                      </nav>
                    ) : (
                      <>
                        <span>THE JOURNAL</span>
                        <p>
                          Ideas, stories
                          <br />& inspiration.
                        </p>
                      </>
                    )}
                    <div className="journal-rail-reading">
                      <FiClock /> {blog.readingMinutes} min read
                    </div>
                    <button onClick={share} aria-label="Share this story">
                      <FiShare2 /> Share this story
                    </button>
                  </aside>
                  <div ref={bodyRef} className="journal-article-body">
                    <BlogRenderer blocks={blog.blocks} />
                    <footer className="journal-article-footer">
                      {blog.tags.length > 0 && (
                        <div className="journal-tags">
                          {blog.tags.map((tag) => (
                            <Link
                              key={tag}
                              to={`/blogs?q=${encodeURIComponent(tag)}`}
                            >
                              #{tag}
                            </Link>
                          ))}
                        </div>
                      )}
                      <div className="journal-endnote">
                        <span>Thanks for reading.</span>
                        <Link to="/blogs">
                          Find your next story <FiArrowUpRight />
                        </Link>
                      </div>
                    </footer>
                  </div>
                </div>
              </article>
              {related.length > 0 && (
                <section className="journal-related">
                  <div className="journal-section-label">
                    <h2>Keep your curiosity moving.</h2>
                    <Link to="/blogs">
                      All stories <FiArrowUpRight />
                    </Link>
                  </div>
                  <div className="journal-grid">
                    {related.map((item) => (
                      <BlogCard key={item.blogId} blog={item} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )
        )}
      </main>
      <Footer />
    </div>
  );
}
